import React from "react";

// Source
import StoreRoute from "../store/Route";
import StoreLocaliation from "../store/Localization";
import StorePreference from "../store/Preference";
import ControllerAlert from "./Alert";
import * as HelperSrc from "../HelperSrc";
import * as ApiLog from "../api/Log";
import * as ModelPagination from "../model/Pagination";
import * as ModelLog from "../model/Log";
import * as ModelRoute from "../model/Route";
import * as ModelLocalization from "../model/Localization";
import * as ModelPreference from "../model/Preference";
import * as ModelHelperSrc from "../model/HelperSrc";

export default class ControllerLogList {
    // Variable
    private storeRoute: ModelRoute.Istore<ModelRoute.Idata>;
    private storageRouteData = React.useRef<ModelRoute.Idata | undefined>(undefined);
    private storeLocalization: ModelLocalization.Istore<ModelLocalization.Idata>;
    private storageLocalizationData = React.useRef<ModelLocalization.Idata | undefined>(undefined);
    private storePreference: ModelPreference.Istore<ModelPreference.Idata>;
    private storagePreferenceData = React.useRef<ModelPreference.Idata | undefined>(undefined);

    private controllerAlert: ControllerAlert;

    private elementLogLevel = React.useRef<HTMLInputElement | null>(null);
    private elementLogSource = React.useRef<HTMLInputElement | null>(null);
    private elementAppName = React.useRef<HTMLInputElement | null>(null);
    private elementCreatedBy = React.useRef<HTMLInputElement | null>(null);
    private elementViewMain = React.useRef<HTMLElement | null>(null);

    private isRendered = React.useRef<boolean>(false);
    private filter = React.useRef<ModelPagination.IfilterLog | undefined>(undefined);
    private dataMainList = React.useRef<ModelLog.Idata[] | undefined>(undefined);
    private dataMain = React.useRef<ModelLog.Idata | undefined>(undefined);
    private language = React.useRef<string>("");

    getApiLoadingList: Record<string, boolean>;
    setApiLoadingList: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;

    getSelectOption: ModelLog.IselectOption | undefined;
    setSelectOption: React.Dispatch<React.SetStateAction<ModelLog.IselectOption | undefined>>;

    getIsDialogOpen: boolean;
    setIsDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;

    getPagination: ModelPagination.Idata | undefined;
    setPagination: React.Dispatch<React.SetStateAction<ModelPagination.Idata | undefined>>;

    getFilter: ModelPagination.IfilterLog | undefined;
    setFilter: React.Dispatch<React.SetStateAction<ModelPagination.IfilterLog | undefined>>;

    getDataMainList: ModelLog.Idata[] | undefined;
    setDataMainList: React.Dispatch<React.SetStateAction<ModelLog.Idata[] | undefined>>;

    getDataMain: ModelLog.Idata | undefined;
    setDataMain: React.Dispatch<React.SetStateAction<ModelLog.Idata | undefined>>;

    getLanguage: string;
    setLanguage: React.Dispatch<React.SetStateAction<string>>;

    // Method
    constructor(isRendered: boolean) {
        this.storeRoute = new StoreRoute();
        this.storageRouteData.current = this.storeRoute.storageGet();
        this.storeLocalization = new StoreLocaliation();
        this.storageLocalizationData.current = this.storeLocalization.storageGet();
        this.storePreference = new StorePreference();
        this.storagePreferenceData.current = this.storePreference.storageGet();

        this.controllerAlert = new ControllerAlert(isRendered);

        this.isRendered.current = isRendered;

        const [getApiLoadingList, setApiLoadingList] = React.useState<Record<string, boolean>>({});
        this.getApiLoadingList = getApiLoadingList;
        this.setApiLoadingList = setApiLoadingList;

        const [getSelectOption, setSelectOption] = React.useState<ModelLog.IselectOption | undefined>(undefined);
        this.getSelectOption = getSelectOption;
        this.setSelectOption = setSelectOption;

        const [getIsDialogOpen, setIsDialogOpen] = React.useState(false);
        this.getIsDialogOpen = getIsDialogOpen;
        this.setIsDialogOpen = setIsDialogOpen;

        const [getPagination, setPagination] = React.useState<ModelPagination.Idata | undefined>(undefined);
        this.getPagination = getPagination;
        this.setPagination = setPagination;

        const [getFilter, setFilter] = React.useState<ModelPagination.IfilterLog | undefined>(this.filter.current);
        this.getFilter = getFilter;
        this.setFilter = setFilter;

        const [getDataMainList, setDataMainList] = React.useState<ModelLog.Idata[] | undefined>(this.dataMainList.current);
        this.getDataMainList = getDataMainList;
        this.setDataMainList = setDataMainList;

        const [getDataMain, setDataMain] = React.useState<ModelLog.Idata | undefined>(this.dataMain.current);
        this.getDataMain = getDataMain;
        this.setDataMain = setDataMain;

        const [getLanguage, setLanguage] = React.useState<string>(this.language.current);
        this.getLanguage = getLanguage;
        this.setLanguage = setLanguage;
    }

    initializeHook = (): void => {
        React.useEffect(() => {
            HelperSrc.domObserver(".view_log_list", () => {
                this.initializeHtmlElement();
            });

            this.apiSelectOption();

            this.apiDataList();
        }, []);

        React.useEffect(() => {
            this.storageRouteData.current = this.storeRoute.storageGet();
            this.storageLocalizationData.current = this.storeLocalization.storageGet();
        }, [this.storageRouteData.current, this.storageLocalizationData.current]);

        React.useEffect(() => {
            this.storagePreferenceData.current = this.storePreference.storageGet();

            if (this.storagePreferenceData.current) {
                const initialLanguage = this.language.current;

                if (this.language.current !== this.storagePreferenceData.current.language) {
                    this.language.current = this.storagePreferenceData.current.language;
                    this.setLanguage(this.language.current);

                    if (initialLanguage !== "") {
                        this.apiSelectOption();
                    }
                }
            }
        }, [this.storagePreferenceData.current]);
    };

    apiDataList = (isFilter = false): void => {
        this.filterParameter(isFilter);

        if (this.filter.current) {
            this.setApiLoadingList((prev) => ({
                ...prev,
                dataList: true
            }));

            ApiLog.dataList(this.filter.current, this.filter.current.sort_order, this.filter.current.page, (response) => {
                this.setApiLoadingList((prev) => ({
                    ...prev,
                    dataList: false
                }));

                if (response instanceof Error || response.message.error) {
                    if (response instanceof Error) {
                        HelperSrc.writeLog("error", response.message);
                    } else {
                        this.controllerAlert.show("error", response.message.error, false);
                    }

                    return;
                }

                this.setPagination(response.pagination);

                this.dataMainList.current = response.pagination.data as unknown as ModelLog.Idata[];
                this.setDataMainList(this.dataMainList.current);

                this.controllerAlert.show("success", response.message.success);
            });
        }
    };

    clickFilterApply = (): void => {
        this.apiDataList(true);
    };

    clickItem = (data: ModelLog.Idata): void => {
        this.setDataMain(data);

        this.setIsDialogOpen(true);
    };

    clickDialogClose = (): void => {
        this.setDataMain(undefined);

        this.setIsDialogOpen(false);
    };

    private initializeHtmlElement = (): void => {
        if (!this.elementViewMain.current) {
            this.elementLogLevel.current = document.querySelector<HTMLInputElement>("[name='level']");
            this.elementLogSource.current = document.querySelector<HTMLInputElement>("[name='source']");
            this.elementAppName.current = document.querySelector<HTMLInputElement>("[name='app_name']");
            this.elementCreatedBy.current = document.querySelector<HTMLInputElement>("[name='created_by']");
            this.elementViewMain.current = document.querySelector<HTMLElement>(".view_log_list");
        }
    };

    private apiSelectOption = (): void => {
        this.setApiLoadingList((prev) => ({
            ...prev,
            selectOption: true
        }));

        ApiLog.selectOption((response) => {
            this.setApiLoadingList((prev) => ({
                ...prev,
                selectOption: false
            }));

            if (response instanceof Error || response.message.error) {
                if (response instanceof Error) {
                    HelperSrc.writeLog("error", response.message);
                } else {
                    this.controllerAlert.show("error", response.message.error, false);
                }

                return;
            }

            this.setSelectOption(response.data.result as unknown as ModelLog.IselectOption);
        });
    };

    private filterParameter = (isFilter: boolean): void => {
        const pageUrl = HelperSrc.urlVariable("page") && !isFilter ? HelperSrc.urlVariable("page") : "1";
        const sortOrderUlr = HelperSrc.urlVariable("sort_order") ? HelperSrc.urlVariable("sort_order") : "desc";
        const levelUrl = HelperSrc.urlVariable("level") && !isFilter ? HelperSrc.urlVariable("level") : "";
        const sourceUrl = HelperSrc.urlVariable("source") && !isFilter ? HelperSrc.urlVariable("source") : "";
        const appNameUrl = HelperSrc.urlVariable("app_name") && !isFilter ? HelperSrc.urlVariable("app_name") : "";
        const createdByUrl = HelperSrc.urlVariable("created_by") && !isFilter ? HelperSrc.urlVariable("created_by") : "";

        const levelResult =
            this.elementLogLevel.current && this.elementLogLevel.current.value !== levelUrl ? this.elementLogLevel.current.value : levelUrl;
        const sourceResult =
            this.elementLogSource.current && this.elementLogSource.current.value !== sourceUrl ? this.elementLogSource.current.value : sourceUrl;
        const appNameResult =
            this.elementAppName.current && this.elementAppName.current.value !== appNameUrl ? this.elementAppName.current.value : appNameUrl;
        const createdByResult =
            this.elementCreatedBy.current && this.elementCreatedBy.current.value !== createdByUrl
                ? this.elementCreatedBy.current.value
                : createdByUrl;

        this.filter.current = {
            page: pageUrl,
            sort_order: sortOrderUlr,
            level: levelResult as string & ModelHelperSrc.IvalidationForm,
            source: sourceResult as string & ModelHelperSrc.IvalidationForm,
            app_name: appNameResult as string & ModelHelperSrc.IvalidationForm,
            created_by: createdByResult as string & ModelHelperSrc.IvalidationForm
        } as ModelPagination.IfilterLog;
        this.setFilter(this.filter.current);

        if (this.storageRouteData.current) {
            HelperSrc.routerHistoryPush(
                `${this.storageRouteData.current.log_list.path}?page=${this.filter.current.page}&sort_order=${this.filter.current.sort_order}&` +
                    `level=${this.filter.current.level}&` +
                    `source=${this.filter.current.source}&` +
                    `app_name=${this.filter.current.app_name}&` +
                    `created_by=${this.filter.current.created_by}`,
                {},
                "",
                true
            );
        }
    };
}
